import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Users, Clock, CalendarDays, Wallet, LogIn, LogOut as LogOutIcon, ArrowRight, Cake, Gift } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

function StatCard({ icon: Icon, label, value, color, delay }) {
  return (
    <div className={`border border-border bg-card shadow-none rounded-sm p-5 page-fade-in stagger-${delay}`} data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] text-muted-foreground uppercase tracking-widest font-body">{label}</span>
        <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${color}`}>
          <Icon className="w-4 h-4" strokeWidth={1.5} />
        </div>
      </div>
      <p className="text-2xl font-heading font-bold text-foreground tracking-tight">{value}</p>
    </div>
  );
}

function AdminDashboard({ stats, anniversaries }) {
  const navigate = useNavigate();
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Users} label="Total Employees" value={stats.employee_count ?? 0} color="bg-blue-50 text-[#002FA7]" delay={1} />
        <StatCard icon={Clock} label="Present Today" value={stats.today_attendance ?? 0} color="bg-emerald-50 text-emerald-600" delay={2} />
        <StatCard icon={CalendarDays} label="Pending Leaves" value={stats.pending_leaves ?? 0} color="bg-amber-50 text-amber-600" delay={3} />
        <StatCard icon={Wallet} label="Last Payroll" value={stats.latest_payroll ? `₹${stats.latest_payroll.total_amount?.toLocaleString('en-IN')}` : 'N/A'} color="bg-violet-50 text-violet-600" delay={4} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="border border-border bg-card rounded-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-foreground">Quick Actions</h3>
          </div>
          <div className="space-y-2">
            <button onClick={() => navigate('/employees')} data-testid="quick-manage-employees" className="w-full flex items-center justify-between px-4 py-3 border border-border rounded-sm hover:bg-accent transition-all duration-200 text-sm font-body text-foreground">
              <span className="flex items-center gap-2"><Users className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} /> Manage Employees</span>
              <ArrowRight className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            </button>
            <button onClick={() => navigate('/payroll')} data-testid="quick-run-payroll" className="w-full flex items-center justify-between px-4 py-3 border border-border rounded-sm hover:bg-accent transition-all duration-200 text-sm font-body text-foreground">
              <span className="flex items-center gap-2"><Wallet className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} /> Run Payroll</span>
              <ArrowRight className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            </button>
            <button onClick={() => navigate('/leaves')} data-testid="quick-review-leaves" className="w-full flex items-center justify-between px-4 py-3 border border-border rounded-sm hover:bg-accent transition-all duration-200 text-sm font-body text-foreground">
              <span className="flex items-center gap-2"><CalendarDays className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} /> Review Leave Requests</span>
              <ArrowRight className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {stats.latest_payroll && (
          <div className="border border-border bg-card rounded-sm p-5">
            <h3 className="font-heading font-semibold text-foreground mb-4">Latest Payroll</h3>
            <div className="space-y-3 text-sm font-body">
              <div className="flex justify-between"><span className="text-muted-foreground">Period</span><span className="font-medium font-mono text-foreground">{stats.latest_payroll.month}/{stats.latest_payroll.year}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Employees</span><span className="font-medium font-mono text-foreground">{stats.latest_payroll.employee_count}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Total Amount</span><span className="font-medium font-mono text-foreground">₹{stats.latest_payroll.total_amount?.toLocaleString('en-IN')}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Status</span><Badge variant={stats.latest_payroll.status === 'finalized' ? 'default' : 'secondary'} className="text-xs">{stats.latest_payroll.status}</Badge></div>
            </div>
          </div>
        )}

        {/* Work Anniversaries */}
        <div className="border border-border bg-card rounded-sm p-5" data-testid="upcoming-anniversaries">
          <div className="flex items-center gap-2 mb-4">
            <Cake className="w-5 h-5 text-pink-500" strokeWidth={1.5} />
            <h3 className="font-heading font-semibold text-foreground">Upcoming Anniversaries</h3>
          </div>
          {anniversaries.length === 0 ? (
            <p className="text-sm text-muted-foreground">No upcoming anniversaries in the next 30 days</p>
          ) : (
            <div className="space-y-3">
              {anniversaries.slice(0, 5).map(ann => (
                <div key={ann.id} className="flex items-center gap-3 py-2 border-b border-border last:border-0">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-100 to-violet-100 flex items-center justify-center">
                    <Gift className="w-5 h-5 text-pink-500" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{ann.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {ann.days_until === 0 ? (
                        <span className="text-pink-500 font-semibold">🎉 Today!</span>
                      ) : (
                        `in ${ann.days_until} day${ann.days_until > 1 ? 's' : ''}`
                      )}
                      {' • '}{ann.years} year{ann.years > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              ))}
              {anniversaries.length > 5 && (
                <p className="text-xs text-muted-foreground text-center pt-2">+{anniversaries.length - 5} more</p>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function EmployeeDashboard({ stats, user }) {
  const navigate = useNavigate();
  const attendance = stats.today_attendance;

  const handleCheckIn = async () => {
    try {
      await api.post('/attendance/check-in');
      toast.success('Checked in successfully');
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Check-in failed');
    }
  };

  const handleCheckOut = async () => {
    try {
      await api.post('/attendance/check-out');
      toast.success('Checked out successfully');
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Check-out failed');
    }
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="border border-border bg-card rounded-sm p-5 page-fade-in stagger-1">
          <span className="text-[11px] text-muted-foreground uppercase tracking-widest font-body block mb-3">Today's Status</span>
          {!attendance ? (
            <Button onClick={handleCheckIn} data-testid="dashboard-check-in-btn" className="bg-[#002FA7] hover:bg-[#002585] text-white rounded-sm text-sm">
              <LogIn className="w-4 h-4 mr-2" strokeWidth={1.5} /> Check In
            </Button>
          ) : !attendance.check_out ? (
            <div>
              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 mb-2">Checked In</Badge>
              <Button onClick={handleCheckOut} data-testid="dashboard-check-out-btn" variant="outline" size="sm" className="rounded-sm mt-1 block">
                <LogOutIcon className="w-3 h-3 mr-1" strokeWidth={1.5} /> Check Out
              </Button>
            </div>
          ) : (
            <div>
              <Badge className="bg-muted text-muted-foreground border-border">Day Complete</Badge>
              <p className="text-xs text-muted-foreground font-mono mt-2">{attendance.hours_worked}h worked</p>
            </div>
          )}
        </div>
        <StatCard icon={CalendarDays} label="Pending Leaves" value={stats.pending_leaves ?? 0} color="bg-amber-50 text-amber-600" delay={2} />
        <StatCard icon={CalendarDays} label="Approved Leaves" value={stats.approved_leaves ?? 0} color="bg-emerald-50 text-emerald-600" delay={3} />
        <StatCard icon={Wallet} label="Latest Net Pay" value={stats.latest_payslip ? `₹${stats.latest_payslip.net_pay?.toLocaleString('en-IN')}` : 'N/A'} color="bg-violet-50 text-violet-600" delay={4} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-border bg-card rounded-sm p-5">
          <h3 className="font-heading font-semibold text-foreground mb-4">Quick Actions</h3>
          <div className="space-y-2">
            <button onClick={() => navigate('/attendance')} className="w-full flex items-center justify-between px-4 py-3 border border-border rounded-sm hover:bg-accent transition-all duration-200 text-sm font-body text-foreground">
              <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} /> View Attendance</span>
              <ArrowRight className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            </button>
            <button onClick={() => navigate('/leaves')} className="w-full flex items-center justify-between px-4 py-3 border border-border rounded-sm hover:bg-accent transition-all duration-200 text-sm font-body text-foreground">
              <span className="flex items-center gap-2"><CalendarDays className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} /> Apply for Leave</span>
              <ArrowRight className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            </button>
            <button onClick={() => navigate('/payslips')} className="w-full flex items-center justify-between px-4 py-3 border border-border rounded-sm hover:bg-accent transition-all duration-200 text-sm font-body text-foreground">
              <span className="flex items-center gap-2"><Wallet className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} /> View Payslips</span>
              <ArrowRight className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {stats.latest_payslip && (
          <div className="border border-border bg-card rounded-sm p-5">
            <h3 className="font-heading font-semibold text-foreground mb-4">Latest Payslip</h3>
            <div className="space-y-3 text-sm font-body">
              <div className="flex justify-between"><span className="text-muted-foreground">Period</span><span className="font-medium font-mono text-foreground">{stats.latest_payslip.month}/{stats.latest_payslip.year}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Gross</span><span className="font-medium font-mono text-foreground">₹{stats.latest_payslip.total_earnings?.toLocaleString('en-IN')}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Deductions</span><span className="font-medium font-mono text-red-500">-₹{stats.latest_payslip.total_deductions?.toLocaleString('en-IN')}</span></div>
              <div className="flex justify-between pt-2 border-t border-border"><span className="text-foreground font-semibold">Net Pay</span><span className="font-bold font-mono text-primary">₹{stats.latest_payslip.net_pay?.toLocaleString('en-IN')}</span></div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [anniversaries, setAnniversaries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const statsRes = await api.get('/dashboard/stats');
        setStats(statsRes.data);
        
        // Fetch anniversaries for admin only
        if (statsRes.data && user?.role === 'admin') {
          try {
            const annRes = await api.get('/employees/upcoming-anniversaries');
            setAnniversaries(annRes.data);
          } catch {
            // Silently fail for anniversaries
          }
        }
      } catch {
        toast.error('Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user?.role]);

  if (loading) return <div className="text-sm text-muted-foreground">Loading dashboard...</div>;

  return (
    <div data-testid="dashboard-page">
      <div className="mb-6">
        <h2 className="text-2xl font-heading font-bold text-foreground tracking-tight">
          {user?.role === 'admin' ? 'Admin Dashboard' : `Welcome, ${user?.name}`}
        </h2>
        <p className="text-sm text-muted-foreground font-body mt-1">
          {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </p>
      </div>

      {user?.role === 'admin' ? <AdminDashboard stats={stats} anniversaries={anniversaries} /> : <EmployeeDashboard stats={stats} user={user} />}
    </div>
  );
}
